import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { IMG, waLink, track } from "@/data/content";
import { Reveal, SectionTag, TituloLineas, FotoRevelada } from "@/components/Reveal";
import PorQue from "@/components/PorQue";

const META_DESC = "Quiénes son CERTERO SRL: empresa boliviana especializada en drones agrícolas DJI Agriculture, con servicio desde 2024 y reseller de DJI Agriculture desde mayo de 2026. Conozca al equipo y la sede en Santa Cruz.";

const MILESTONES = [
  {
    year: "2024",
    t: "Enero — Primeras operaciones",
    d: "Inicio de servicios con drones de pulverización y dispersión de sólidos en Santa Cruz y Beni.",
  },
  {
    year: "2026",
    t: "Mayo — Reseller DJI Agriculture",
    d: "CERTERO se convierte en reseller de DJI Agriculture en Bolivia, con la línea Agras y el Mavic 3M.",
  },
];

const EQUIPO = [
  { id: "carlos-quiroga", src: IMG.carlosQuiroga, nombre: "Carlos Quiroga Sivila", cargo: "Gerente General" },
  { id: "anna-rodriguez", src: IMG.annaRodriguez, nombre: "Anna Rodríguez", cargo: "Gerente de marketing y operaciones" },
  { id: "bernardo-quiroga", src: IMG.bernardoQuiroga, nombre: "Bernardo Quiroga", cargo: "Encargado de gestión y pilotaje" },
  { id: "augusto-languidey", src: IMG.augustoLanguidey, nombre: "Augusto Languidey", cargo: "Técnico en logística" },
  { id: "fernando-hurtado", src: IMG.fernandoHurtado, nombre: "Fernando Hurtado", cargo: "Encargado del servicio técnico y posventa" },
];

const GALERIA = [
  { src: IMG.equipoPrincipal, alt: "Equipo de CERTERO SRL con un dron Agras y la bandera de DJI Agriculture en campo" },
  { src: IMG.capacitacionPosventa, alt: "Entrega de un dron Agras con capacitación al equipo del productor en depósito" },
  { src: IMG.capacitacionProductor, alt: "El equipo de CERTERO SRL capacita a un productor en el manejo del control de un dron Agras" },
  { src: IMG.operadorCampo, alt: "Operador de CERTERO SRL pilotando un dron Agras al atardecer" },
];

const SEDE = [
  { src: IMG.sedeRecepcion, alt: "Recepción de la sede de CERTERO SRL en Santa Cruz de la Sierra" },
  { src: IMG.sedeOficina, alt: "Oficina comercial de CERTERO SRL" },
  { src: IMG.sedeFachada, alt: "Fachada de la sede de CERTERO SRL, Av. Clara Cuta n.º 68, Santa Cruz de la Sierra" },
];

function PersonCard({ p, delay, grande = false }) {
  return (
    <Reveal delay={delay} data-testid={`person-card-${p.id}`}>
      <figure className="group relative overflow-hidden border border-hud transition-colors duration-300 hover:border-certero/50">
        <FotoRevelada src={p.src} alt={`${p.nombre}, ${p.cargo} de CERTERO SRL`} className="aspect-[4/5] w-full" />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#060b12] via-[#060b12]/55 to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className={`font-display font-bold uppercase leading-snug tracking-wide text-mist ${grande ? "text-xl sm:text-2xl" : "text-base sm:text-lg"}`}>
            {p.nombre}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase leading-snug tracking-[0.2em] text-certero">{p.cargo}</p>
        </div>
      </figure>
    </Reveal>
  );
}

export default function NosotrosPage() {
  useEffect(() => {
    const prev = { t: document.title, d: document.querySelector('meta[name="description"]')?.content, c: document.querySelector('link[rel="canonical"]')?.href };
    document.title = "Nosotros | CERTERO SRL";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.content = META_DESC;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://certerosrl.com/nosotros";
    return () => {
      document.title = prev.t;
      if (meta && prev.d) meta.content = prev.d;
      if (link && prev.c) link.href = prev.c;
    };
  }, []);

  return (
    <div className="bg-abyss px-4 pb-24 pt-28 sm:px-8 lg:px-12 lg:pt-32">
      <div className="mx-auto max-w-[1400px]" data-testid="nosotros-page">
        <nav aria-label="Ruta de navegación" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke" data-testid="nosotros-breadcrumb">
          <Link to="/" className="transition-colors duration-300 hover:text-certero">Inicio</Link>
          <span aria-hidden="true">›</span>
          <span className="text-certero">Nosotros</span>
        </nav>

        {/* Portada */}
        <Reveal className="mt-8">
          <FotoRevelada
            src={IMG.equipoPrincipal}
            alt="Equipo de CERTERO SRL con un dron Agras y la bandera de DJI Agriculture en campo"
            kenburns
            className="aspect-[16/9] w-full border border-hud"
            loading="eager"
          />
        </Reveal>

        {/* Historia */}
        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <Reveal delay={0.06} className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">Quiénes somos</p>
            <TituloLineas
              as="h1"
              lineas={["Especialistas en drones agrícolas,", "con los pies en el campo"]}
              className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-mist sm:text-4xl lg:text-5xl"
            />
            <div className="mt-6 flex flex-col gap-4 text-sm leading-relaxed text-smoke sm:text-base">
              <p>
                CERTERO SRL es una empresa boliviana especializada en drones agrícolas DJI
                Agriculture, soluciones con drones, servicios agrícolas, soporte técnico,
                capacitación y tecnología aplicada al campo.
              </p>
              <p>
                Desde Santa Cruz de la Sierra trabajamos con productores, empresas agrícolas,
                ingenieros e instituciones, prestando servicio con drones desde enero de 2024 y
                como resellers de DJI Agriculture desde mayo de 2026.
              </p>
              <p>
                Nuestra cobertura abarca todo el departamento de Santa Cruz — norte, este, sur,
                Chiquitania y frontera con Brasil — y los departamentos del Beni y de Cochabamba,
                con la mirada puesta en grandes proyectos en todo Bolivia.
              </p>
            </div>
          </Reveal>

          {/* Sello + línea de tiempo */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="flex items-center gap-5 border border-certero/40 bg-ink p-6" data-testid="nosotros-sello-reseller">
                <img src="/logo-color.svg" alt="Isotipo CERTERO" className="h-12 w-auto" loading="lazy" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-certero">Reseller DJI Agriculture</p>
                  <p className="mt-1 text-sm text-mist/85">Reseller de DJI Agriculture en Bolivia desde mayo de 2026.</p>
                </div>
              </div>
            </Reveal>
            <div className="mt-8 flex flex-col" data-testid="nosotros-timeline">
              {MILESTONES.map((m, i) => (
                <Reveal key={m.year} delay={0.12 * (i + 1)}>
                  <div className="relative border-l border-hud pb-10 pl-8 last:pb-0">
                    <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-certero" aria-hidden="true" />
                    <span className="font-mono text-xs tracking-[0.25em] text-certero">{m.year}</span>
                    <h2 className="mt-2 font-display text-lg font-bold uppercase tracking-wide text-mist">{m.t}</h2>
                    <p className="mt-1 text-sm leading-relaxed text-smoke">{m.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Equipo completo */}
        <div className="mt-16">
          <Reveal>
            <h2 className="font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">El equipo</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 min-[900px]:grid-cols-3" data-testid="nosotros-equipo-fila">
            <div className="md:col-span-2 min-[900px]:col-span-1">
              <PersonCard p={EQUIPO[0]} grande />
            </div>
            <PersonCard p={EQUIPO[1]} delay={0.08} />
            <PersonCard p={EQUIPO[2]} delay={0.12} />
            <PersonCard p={EQUIPO[3]} delay={0.16} />
            <PersonCard p={EQUIPO[4]} delay={0.2} />
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="nosotros-team-chips">
              {["Equipo especializado en soporte y asesoría agronómica"].map((c) => (
                <span key={c} className="flex items-center gap-2 border border-hud bg-ink px-4 py-2 text-xs text-mist/85">
                  <CheckCircle2 className="h-3.5 w-3.5 text-certero" /> {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Galería del equipo */}
        <Reveal delay={0.1} className="mt-14">
          <div data-testid="nosotros-gallery-row" className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {GALERIA.map((g) => (
              <img key={g.src} src={g.src} alt={g.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full border border-hud object-cover" />
            ))}
          </div>
        </Reveal>

        {/* Nuestra sede */}
        <Reveal delay={0.15} className="mt-14">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-certero" data-testid="nosotros-sede-titulo">Nuestra sede</p>
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
            {SEDE.map((s) => (
              <figure key={s.src} className="overflow-hidden border border-hud">
                <FotoRevelada src={s.src} alt={s.alt} className="aspect-[4/3] w-full" />
                <div className="flex items-center justify-between gap-2 bg-ink px-3 py-2">
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-smoke">Operación CERTERO</p>
                </div>
              </figure>
            ))}
          </div>
        </Reveal>

        {/* Por qué CERTERO (viene de la portada) */}
        <div className="mt-16">
          <PorQue />
        </div>

        {/* Cierre */}
        <Reveal className="mt-16">
          <div className="flex flex-col items-start gap-6 border border-certero/40 bg-[#0c295c]/40 p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between" data-testid="nosotros-cierre">
            <div>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">¿Conversamos?</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
                Escríbanos y le asesoramos para elegir el equipo y el servicio correctos para su cultivo.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <a
                data-testid="nosotros-btn-whatsapp"
                href={waLink("Hola CERTERO SRL, quiero asesoría para elegir un dron agrícola.")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { origen: "pagina_nosotros" })}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
              >
                <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
              </a>
              <Link
                data-testid="nosotros-btn-drones"
                to="/#drones"
                onClick={() => track("ver_drones_click", { origen: "pagina_nosotros" })}
                className="group inline-flex h-12 items-center gap-2 rounded-full border border-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
              >
                Ver drones <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
